/* -----------------------------
    BASE MEDIA CLASS
   ----------------------------- */

class BaseMedia extends HTMLElement {
  constructor() {
    super();
  }

  static get observedAttributes() {
    return ["playing"];
  }

  attributes = {
    playing: "playing",
    autoplay: "autoplay",
    loaded: "loaded",
    host: "host",
    mute: "mute",
    group: "group"
  };

  hosts = {
    youtube: "youtube"
  };

  get player() {
    return this.playerProxy = this.playerProxy || new Proxy(this.playerTarget(), {
      get: (target, prop) => {
        return async () => {
          target = await target;
          this.playerHandler(target, prop);
        };
      }
    });
  }

  get playing() {
    return this.hasAttribute(this.attributes.playing);
  }

  toggle() {
    if (!this.playing) {
      this.player.play();
    } else {
      this.player.pause();
    }
  }

  play() {
    if (!this.playing) {
      this.player.play();
    }
  }

  pause() {
    if (this.playing) {
      this.player.pause();
    }
  }

  connectedCallback() {
    if (this.hasAttribute(this.attributes.autoplay)) {
      this.play();
    }
  }

  attributeChangedCallback(name, oldValue, newValue) {
    if (name === this.attributes.playing) {
      if (oldValue === null && newValue === "") {
        this.dispatchEvent(new CustomEvent("media:play", { bubbles: true }));

        if (this.hasAttribute(this.attributes.group)) {
          const groupId = this.getAttribute("group");
          const groupedItems = [...document.querySelectorAll(`[group="${groupId}"]`)];

          if (groupedItems.length) {
            groupedItems.forEach((itemToPause) => {
              if (itemToPause === this) {
                return;
              }

              itemToPause.pause();
            });
          }
        }
      } else if (newValue === null) {
        this.dispatchEvent(new CustomEvent("media:pause", { bubbles: true }));
      }
    }
  }
}

/* -----------------------------
    VIDEO MEDIA COMPONENT
   ----------------------------- */

class VideoMedia extends BaseMedia {
 constructor() {
   super();
 }

  connectedCallback() {
    super.connectedCallback();

    if (!this.hasAttribute(this.attributes.autoplay)) {
      this.addEventListener("click", this.toggle);
    }
  }

  playerTarget() {
    const host = this.getAttribute("host");

    if (host) {
      return this.initExternalPlayer(host);
    } else {
      return this.initNativePlayer();
    }
  }

  playerHandler(target, prop) {
    if (this.getAttribute(this.attributes.host) === this.hosts.youtube) {
      prop === "play" ? target.playVideo() : target.pauseVideo();
    } else {
      target[prop]();
    }
  }

  initExternalPlayer(host) {
    const templateElement = this.querySelector("template");

    if (templateElement) {
      templateElement.replaceWith(templateElement.content.firstElementChild.cloneNode(true));
    }

    this.setAttribute(this.attributes.loaded, "");

    return new Promise(async (resolve) => {
      if (host === this.hosts.youtube) {
        await this.loadYoutubeScript();

        const player = new YT.Player(this.querySelector("iframe"), {
          events: {
            "onReady": () => {
              if (this.getAttribute(this.attributes.autoplay)) {
                player.mute();
              }

              resolve(player);
            },
            "onStateChange": (event) => {
              if (event.data === YT.PlayerState.PLAYING) {
                this.setAttribute("playing", "");
              } else if (event.data === YT.PlayerState.ENDED || event.data === YT.PlayerState.PAUSED) {
                this.removeAttribute("playing");
              }
            }
          }
        });
      }
    });
  }

  initNativePlayer() {
    const videoElement = this.querySelector("video");

    this.setAttribute(this.attributes.loaded, "");

    videoElement.addEventListener("play", () => {
      this.setAttribute(this.attributes.playing, "");
    });

    videoElement.addEventListener("pause", () => {
      if (!videoElement.seeking && videoElement.paused) {
        this.removeAttribute(this.attributes.playing);
      }
    });

    videoElement.addEventListener("ended", () => {
      videoElement.load();
    });

    return videoElement;
  }

  async loadYoutubeScript() {
    return new Promise(async (resolve) => {
      const script = document.createElement("script");
      script.type = "text/javascript";

      if (!window.YT || !window.YT.Player) {
        script.src = "https://www.youtube.com/iframe_api";
        document.head.appendChild(script);

        await new Promise((resolve2) => {
          script.onload = resolve2;
        });

        await new Promise((resolve3) => {
          window.onYouTubeIframeAPIReady = () => resolve3();
        });

        resolve();
      }
    });
  }
}

if (!customElements.get("video-media")) {
  customElements.define("video-media", VideoMedia);
}

/* -----------------------------
    MODEL MEDIA COMPONENT
   ----------------------------- */

class ModelMedia extends BaseMedia {
  constructor() {
    super();
  }

  connectedCallback() {
    super.connectedCallback();
  }

  playerTarget() {
    return new Promise((resolve) => {
      this.setAttribute("loaded", "");
      window.Shopify.loadFeatures([
        {
          name: "model-viewer-ui",
          version: "1.0",
          onLoad: () => {
            const modelViewer = this.querySelector("model-viewer");
            modelViewer.addEventListener("shopify_model_viewer_ui_toggle_play", () => this.setAttribute("playing", ""));
            modelViewer.addEventListener("shopify_model_viewer_ui_toggle_pause", () => this.removeAttribute("playing"));
            this.setAttribute("can-play", "");
            resolve(new window.Shopify.ModelViewerUI(modelViewer, { focusOnPlay: true }));
          }
        }
      ]);
    });
  }

  playerHandler(target, prop) {
    target[prop]();
  }
}

if (!customElements.get("model-media")) {
  customElements.define("model-media", ModelMedia);
}


/* -----------------------------
    CUSTOM ZOOM
   ----------------------------- */

class ZoomMedia extends HTMLElement {
  constructor() {
    super();

    this.onClickListener = this.onClick.bind(this);

    this.onPointerMoveListener = this.onPointerMove.bind(this);
    this.onTouchMoveListener = this.onTouchMove.bind(this);
  }

  static get observedAttributes() {
    return ["zoomed"];
  }

  scale = 1;
  imageElement = null;

  attributes = {
    zoomed: "zoomed"
  };

  get isZoomed() {
    return this.hasAttribute(this.attributes.zoomed);
  }

  connectedCallback() {
    document.addEventListener("click", this.onClickListener);

    this.imageElement = this.querySelector("img");
  }

  disconnectedCallback() {
    document.removeEventListener("click", this.onClickListener);
  }

  attributeChangedCallback(name, oldValue, newValue) {
    if (name === this.attributes.zoomed) {
      if (oldValue === null && newValue === "") {
        this.dispatchEvent(new CustomEvent("zoom:in", { bubbles: true }));
      } else if (newValue === null) {
        this.dispatchEvent(new CustomEvent("zoom:out", { bubbles: true }));
      }
    }
  }

  zoomIn() {
    this.addEventListener("pointermove", this.onPointerMoveListener);
    this.addEventListener("touchmove", this.onTouchMoveListener);

    this.scale = 2;
    this.imageElement.style.transform = `translate3d(0, 0, 0) scale(${this.scale})`;

    this.setAttribute(this.attributes.zoomed, "");
  }

  zoomOut() {
    this.removeEventListener("pointermove", this.onPointerMoveListener);
    this.removeEventListener("touchmove", this.onTouchMoveListener);

    this.scale = 1;
    this.imageElement.style.transform = `translate3d(0, 0, 0) scale(${this.scale})`;

    this.removeAttribute(this.attributes.zoomed);
  }

  onClick(event) {
    if (!this.contains(event.target)) {
      this.zoomOut();
      return;
    }

    if (this.isZoomed) {
      this.zoomOut();
    } else {
      this.zoomIn();
    }
  }

  onPointerMove(event) {
    if (!this.isZoomed) {
      return;
    }

    const containerRect = this.imageElement.getBoundingClientRect();

    const x = event.clientX - containerRect.left;
    const y = event.clientY - containerRect.top;

    const offsetX = x / containerRect.width;
    const offsetY = y / containerRect.height;

    this.updateTransformOrigin(offsetX, offsetY);
  }

  onTouchMove(event) {
    if (!this.isZoomed) {
      return;
    }

    event.preventDefault();

    const containerRect = this.imageElement.getBoundingClientRect();
    const touch = event.touches[0];

    const x = touch.clientX - containerRect.left;
    const y = touch.clientY - containerRect.top;

    const offsetX = x / containerRect.width;
    const offsetY = y / containerRect.height;

    const clampedOffsetX = Math.max(0, Math.min(offsetX, 1));
    const clampedOffsetY = Math.max(0, Math.min(offsetY, 1));

    this.updateTransformOrigin(clampedOffsetX, clampedOffsetY);
  }

  updateTransformOrigin(offsetX, offsetY) {
    this.imageElement.style.transformOrigin = `${offsetX * 100}% ${offsetY * 100}%`;
  }
}

if (!customElements.get("zoom-media")) {
  customElements.define("zoom-media", ZoomMedia);
}
